import React, { useEffect, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useLang } from '../i18n/LanguageContext';

const LandingNavbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { lang, toggleLang, tr } = useLang();
  // CM photo and TN logo show only at the top of the page; hidden once scrolled.
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  // Tamil labels are much wider — switch to the hamburger menu one breakpoint
  // earlier so the title and links never wrap into each other.
  const isTa = lang === 'ta';
  const desktopNavCls = isTa ? 'hidden xl:flex' : 'hidden lg:flex';
  const mobileOnlyCls = isTa ? 'xl:hidden' : 'lg:hidden';
  const menuItems = [
    { 
      name: 'Home',
      label: ['Home', 'முகப்பு'],
      href: '/', 
      icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg>
    },
    { 
      name: 'Workflow',
      label: ['Workflow', 'செயல்முறை'],
      href: '/application-workflow', 
      icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v6a2 2 0 002 2h2m0 0h2m-2 0v4m6-6h2a2 2 0 002-2V7a2 2 0 00-2-2h-2m0 0V3m0 2h2m-2 0h-2m2 0V3" /></svg>
    },
    { 
      name: 'Documents',
      label: ['Documents', 'ஆவணங்கள்'],
      href: '/documents', 
      icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
    },
    { 
      name: 'Guidelines',
      label: ['Guidelines', 'வழிகாட்டுதல்கள்'],
      href: '/guidelines', 
      icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" /></svg>
    },
 
    { 
      name: 'FAQ',
      label: ['FAQ', 'கேள்வி-பதில்'],
      href: '/faq', 
      icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
    },
    { 
      name: 'Contact',
      label: ['Contact', 'தொடர்பு'],
      href: '/contact', 
      icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
    }
  ];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
        
        .navbar-font {
          font-family: 'Inter', system-ui, sans-serif;
        }
      `}</style>
      
      <nav className="bg-brand-800 border-b-2 border-brand-accent shadow-lg fixed w-full top-0 z-50 navbar-font">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo and Title Section */}
            <div className="flex items-center space-x-4 flex-shrink-0">
              {/* Hon'ble Chief Minister — hidden until /images/cm.png exists */}
              <img src="/images/cm.png" alt="Hon'ble Chief Minister of Tamil Nadu" className={`${scrolled ? 'hidden' : ''} h-14 w-14 sm:h-[72px] sm:w-[72px] rounded-full object-cover object-top border-2 border-white/70 flex-shrink-0`} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
              {/* Title */}
              <div className="hidden sm:block leading-tight whitespace-nowrap">
                <h1 className="text-lg lg:text-xl font-semibold text-white">
                 ஏரி இணையதளம்
                </h1>
                <p className="text-[0.9rem] text-brand-100 tracking-wide">
                  தமிழ்நாடு அரசு
                </p>
              </div>
            </div>

            {/* Desktop Navigation */}
            <div className={`${desktopNavCls} items-center gap-0.5 mx-3`}>
              {menuItems.map((item) => (
                <NavLink
                  key={item.name}
                  to={item.href}
                  end={item.href === '/'}
                  className={({ isActive }) =>
                    `flex items-center space-x-1 ${isTa ? 'px-2 text-[13px]' : 'px-3 text-sm'} py-2 rounded-md font-medium whitespace-nowrap transition-all duration-200 ${
                      isActive
                        ? 'text-white bg-white/15'
                        : 'text-brand-100 hover:text-white hover:bg-white/10'
                    }`
                  }
                >
                  {item.icon}
                  <span>{tr(...item.label)}</span>
                </NavLink>
              ))}
            </div>

            {/* Auth Buttons */}
            <div className="flex items-center space-x-4 flex-shrink-0">
              {/* Language Toggle */}
              <button
                type="button"
                onClick={toggleLang}
                className="hidden md:flex items-center space-x-1 text-brand-100 hover:text-white hover:bg-white/10 px-3 py-2 text-sm font-medium transition-colors border border-white/20"
                title={lang === 'en' ? 'தமிழில் காண்க' : 'View in English'}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                </svg>
                <span>{lang === 'en' ? 'தமிழ்' : 'English'}</span>
              </button>

              {/* Login Button with Icon */}
              <Link 
                to="/login" 
                className="flex items-center space-x-1 bg-white hover:bg-brand-50 text-brand-800 px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-all duration-200 shadow-md"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                </svg>
                <span>{tr('Login', 'உள்நுழைய')}</span>
              </Link>

              {/* Mobile menu button */}
              <div className={mobileOnlyCls}>
                <button
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  className="text-brand-100 hover:text-white focus:outline-none p-2 hover:bg-white/10 rounded-md transition-colors"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    {isMenuOpen ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                    )}
                  </svg>
                </button>
              </div>
              {/* TN Government logo — right corner */}
              <div className={`${scrolled ? 'hidden' : ''} flex-shrink-0`}>
             <img 
  src="/images/tnlogo.png"
  alt="Logo" 
  className="h-14 w-14 sm:h-[72px] sm:w-[72px] object-contain rounded-full bg-white border-2 border-white/70 p-1"
  onError={(e) => {
    e.target.style.display = 'none';
    e.target.nextElementSibling.style.display = 'flex';
  }}
/>
<div className="w-[72px] h-[72px] flex items-center justify-center" style={{display: 'none'}}>
  <div className="text-center">
    <div className="text-white font-bold text-xs">GOVT OF</div>
    <div className="text-white font-bold text-xs">TN</div>
  </div>
</div>
              </div>
            </div>
          </div>

          {/* Mobile Navigation Menu */}
          {isMenuOpen && (
            <div className={`${mobileOnlyCls} bg-brand-900 border-t border-white/20`}>
              <div className="px-2 pt-2 pb-3 space-y-1">
                {/* Mobile Title */}
                <div className="sm:hidden px-3 py-2 border-b border-white/20 mb-2">
                  <h1 className="text-lg font-semibold text-white">
                    ஏரி இணையதளம்
                  </h1>
                </div>
                
                {/* Menu Items */}
                {menuItems.map((item) => (
                  <NavLink
                    key={item.name}
                    to={item.href}
                    end={item.href === '/'}
                    className={({ isActive }) =>
                      `flex items-center space-x-2 px-3 py-2 rounded-md text-base font-medium transition-colors ${
                        isActive
                          ? 'text-white bg-white/15'
                          : 'text-brand-100 hover:text-white hover:bg-white/10'
                      }`
                    }
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.icon}
                    <span>{tr(...item.label)}</span>
                  </NavLink>
                ))}

                {/* Mobile Language Toggle */}
                <div className="pt-2 border-t border-white/20">
                  <button
                    type="button"
                    onClick={toggleLang}
                    className="flex items-center space-x-2 w-full px-3 py-2 text-brand-100 hover:text-white hover:bg-white/10 text-base font-medium transition-colors"
                  >
                    <span>{lang === 'en' ? 'தமிழில் காண்க' : 'View in English'}</span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </nav>
    </>
  );
};

export default LandingNavbar;